import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BrandLogo from "./BrandLogo";
import { HERO_AUDIENCE_WORDS } from "../../data/landingContent";
import { IconArrow } from "./icons";

/** Orbit modules — clockwise from 12 o'clock (EMR → follow-up full cycle). */
const ORBIT_MODULES = [
  { icon: "🩺", label: "EMR" },
  { icon: "🧑‍⚕️", label: "OPD" },
  { icon: "🎙️", label: "Voice AI", accent: true },
  { icon: "🛏️", label: "IPD" },
  { icon: "💊", label: "e-Prescription" },
  { icon: "🩻", label: "Radiology" },
  { icon: "🥗", label: "Nutritional" },
  { icon: "💗", label: "Physiotherapy" },
  { icon: "🧾", label: "Billing & Follow-up" },
  { icon: "🛡️", label: "Insurance AI", accent: true },
];

/** Compact trust marks for hero (logos preferred; ISO omitted). */
const TRUST_MARKS = [
  {
    label: "HIPAA-aligned",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3l7 3v5c0 4.5-3 7.8-7 9-4-1.2-7-4.5-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M11.2 8.5h1.6v7h-1.6zm-2.6 2.8h6.8v1.5H8.6z" fill="currentColor" />
      </svg>
    ),
  },
  {
    label: "AWS",
    icon: (
      <svg width="22" height="14" viewBox="0 0 40 24" aria-hidden="true">
        <text x="0" y="14" fill="currentColor" fontSize="13" fontWeight="700" fontFamily="Arial, sans-serif">
          aws
        </text>
        <path fill="none" stroke="currentColor" strokeWidth="1.5" d="M16 16c3.5 2.4 9 2.4 12.5 0" />
      </svg>
    ),
  },
  {
    label: "ABDM-ready",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="12" cy="12" r="4.8" stroke="currentColor" strokeWidth="1.3" />
        <path fill="currentColor" d="M12 7.5c.7 1.1 1.9 1.8 3.2 2-0.6 1.8-1.8 3.1-3.2 3.8-1.4-.7-2.6-2-3.2-3.8 1.3-.2 2.5-.9 3.2-2z" />
      </svg>
    ),
  },
  {
    label: "NHA",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="12" cy="12" r="2" fill="currentColor" />
        <path
          stroke="currentColor"
          strokeWidth="1.2"
          d="M12 5v2.2M12 16.8V19M5 12h2.2M16.8 12H19M7.2 7.2l1.5 1.5M15.3 15.3l1.5 1.5M16.8 7.2l-1.5 1.5M8.7 15.3l-1.5 1.5"
        />
      </svg>
    ),
  },
];

const CYCLE_MS = 3200;

/** Equal arc spacing: index 0 at 12 o'clock, then clockwise. */
function orbitAngle(index, total) {
  return -90 + (360 / total) * index;
}

export default function Hero() {
  const [wordIdx, setWordIdx] = useState(0);
  const [swapping, setSwapping] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;

    const id = window.setInterval(() => {
      setSwapping(true);
      window.setTimeout(() => {
        setWordIdx((i) => (i + 1) % HERO_AUDIENCE_WORDS.length);
        setSwapping(false);
      }, 320);
    }, CYCLE_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <section className="hero" id="platform" aria-label="Introduction">
      <div className="copy">
        <span className="eyebrow">
          <span className="pulse" aria-hidden="true" />
          AI Hospital OS · ABHA Supported
        </span>

        <h1 className="hero-title">
            <span className="hero-title-line">AI Operating System</span>
            <span className="hero-title-line">
              for every{" "}
              <span className="cycle-wrap" aria-live="polite">
                <span className={`cycle ${swapping ? "swap" : ""}`}>
                  {HERO_AUDIENCE_WORDS[wordIdx]}
                </span>
              </span>
            </span>
          </h1>

        <p className="sub">
          Hospital EMR software for Indian clinics and hospitals — OPD, IPD, Pharmacy, Billing,
          Physiotherapy, and Nutrition on one ABDM-ready patient record. Desktop and mobile.
        </p>

        <p className="hero-doctors-count">
          Built for <strong>Multi-Specialty, Orthopedic, Chronic Disease, and more</strong> practices across India
        </p>

        <div className="cta-row">
          <Link to="/demo" className="btn-primary">
            Schedule Demo <IconArrow />
          </Link>
          <Link to="/products" className="btn-ghost">
            <span className="ring" aria-hidden="true" />
            Explore Products
          </Link>
        </div>

        <div className="trust-row">
          {TRUST_MARKS.map((mark, i) => (
            <span key={mark.label} className="trust-chunk">
              {i > 0 ? <span className="trust-sep" aria-hidden="true" /> : null}
              <span className="trust-item">
                {mark.icon}
                {mark.label}
              </span>
            </span>
          ))}
        </div>
      </div>

      <div className="orbit-col">
        <div className="orbit-wrap" aria-hidden="true">
          <div className="ring r1" />
          <div className="ring r2" />
          <div className="ring r3" />

          <div className="core core-logo core-branded">
            <BrandLogo variant="mark" className="core-brand-logo" priority />
            <div className="core-brand-name">Deepaarogya AI</div>
            <div className="tag-sub tag-sub-live">AI OS · LIVE</div>
          </div>

          <div className="orbiter">
            {ORBIT_MODULES.map((n, i) => (
              <div
                key={n.label}
                className={`node ${n.accent ? "accent" : ""}`}
                style={{ "--orbit-angle": `${orbitAngle(i, ORBIT_MODULES.length)}deg` }}
              >
                <span className="ic">{n.icon}</span>
                {n.label}
              </div>
            ))}
          </div>
        </div>

        <div className="orbit-caption">
          <div className="k">Deepaarogya AI</div>
          <div className="m">
            One platform. <b>Every department.</b>
          </div>
        </div>
      </div>
    </section>
  );
}
